import { FC, ReactNode, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.scss';

export interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'outlineLight';
  size?: 'sm' | 'md' | 'lg';
  to?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  fullWidth?: boolean;
  target?: string;
  rel?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

const Button: FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  type = 'button',
  onClick,
  className = '',
  fullWidth = false,
  target,
  rel,
  disabled = false,
  'aria-label': ariaLabel,
}) => {
  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Internal Router Link
  if (to && !disabled) {
    return (
      <Link
        to={to}
        className={buttonClasses}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  // External or absolute hyperlink
  if (href && !disabled) {
    return (
      <a
        href={href}
        className={buttonClasses}
        onClick={onClick}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : rel}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }

  // Standard Button
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
};

export default Button;
